import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import AppNavPublic from '@/components/layout/AppNavPublic';
import { VeracordLogo } from '@/components/ui/VeracordLogo';


// ─────────────────────────────────────────────
// MESH GRADIENT (same as FAQ / Hero)
// ─────────────────────────────────────────────
const MeshGradient = () => (
  <>
    <style>{`
      @keyframes meshShift {
        0%   { background-position: 0% 0%; }
        25%  { background-position: 80% 20%; }
        50%  { background-position: 100% 100%; }
        75%  { background-position: 20% 80%; }
        100% { background-position: 0% 0%; }
      }
      @keyframes orb1 {
        0%,100% { transform: translate(0,0) scale(1); }
        33%     { transform: translate(120px,80px) scale(1.35); }
        66%     { transform: translate(-60px,40px) scale(0.85); }
      }
      @keyframes orb2 {
        0%,100% { transform: translate(0,0) scale(1); }
        33%     { transform: translate(-90px,110px) scale(1.45); }
        66%     { transform: translate(70px,-50px) scale(0.8); }
      }
      @keyframes orb3 {
        0%,100% { transform: translate(0,0) scale(1); }
        33%     { transform: translate(-100px,-70px) scale(1.3); }
        66%     { transform: translate(60px,90px) scale(1.1); }
      }
      @keyframes orb4 {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(80px,-60px) scale(1.2); }
      }
    `}</style>

    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(135deg, #000223 0%, #02075d 45%, #1a1a4e 70%, #c8cfe8 100%)',
        backgroundSize: '300% 300%',
        animation: 'meshShift 18s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', top: '-10%', left: '-5%',
        width: '60%', height: '60%', borderRadius: '50%',
        filter: 'blur(70px)', animation: 'orb1 22s ease-in-out infinite',
        background: 'radial-gradient(circle, rgba(11,26,142,0.1) 0%, #02042a 45%, #0e0221 70%)',
      }} />
      <div style={{
        position: 'absolute', top: '20%', left: '20%',
        width: '65%', height: '65%', borderRadius: '50%',
        filter: 'blur(90px)', animation: 'orb2 28s ease-in-out infinite',
        background: 'radial-gradient(circle, #0a166d 0%, rgba(10,20,120,0.3) 40%, transparent 70%)',
      }} />
      <div style={{
        position: 'absolute', bottom: '-15%', right: '-5%',
        width: '70%', height: '70%', borderRadius: '50%',
        filter: 'blur(100px)', animation: 'orb3 20s ease-in-out infinite',
        background: 'radial-gradient(circle, rgba(220,225,255,0.45) 0%, rgba(160,180,240,0.3) 35%, transparent 70%)',
      }} />
      <div style={{
        position: 'absolute', top: '40%', left: '-10%',
        width: '50%', height: '50%', borderRadius: '50%',
        filter: 'blur(80px)', animation: 'orb4 24s ease-in-out infinite',
        background: 'radial-gradient(circle, rgba(3,79,100,0.05) 0%, transparent 70%)',
      }} />
      <div style={{
        position: 'absolute', top: '25%', right: '0%',
        width: '40%', height: '40%', borderRadius: '50%',
        filter: 'blur(70px)', animation: 'orb2 26s ease-in-out infinite reverse',
        background: 'radial-gradient(circle, rgba(120,140,255,0.3) 0%, transparent 70%)',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        opacity: 0.04, mixBlendMode: 'overlay',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        backgroundSize: '200px 200px',
      }} />
    </div>
  </>
);

const TRUST_BULLETS = [
  'Audit-ready in 30 seconds',
  'Cancel anytime',
  'Setup in under an hour',
];

export default function GetStarted() {
  const navigate = useNavigate();
  const [clinicName, setClinicName] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedClinic = clinicName.trim();

    if (!trimmedEmail || !trimmedClinic) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) return;

    navigate(`/onboarding?email=${encodeURIComponent(trimmedEmail)}&clinic_name=${encodeURIComponent(trimmedClinic)}`);
  };

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: '#000223' }}>
      <MeshGradient />
      <AppNavPublic />

      <div className="relative z-10 flex flex-col items-center justify-center px-4 pt-32 pb-20">
        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
          className="text-center max-w-xl mx-auto mb-8"
        >
          <h1
            className="mb-4"
            style={{
              fontFamily: 'Source Serif Pro, Georgia, serif',
              fontWeight: 900,
              fontSize: 'clamp(28px, 5vw, 42px)',
              color: '#ffffff',
              lineHeight: 1.15,
              letterSpacing: '-0.01em',
            }}
          >
            Start Your 30-Second Audit.
          </h1>
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '15px',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.6,
            }}
          >
            One clinic. Unlimited policies. Unlimited staff.{' '}
            <span style={{ color: '#ffffff', fontWeight: 600 }}>$249/month.</span>
          </p>
        </motion.div>

        {/* Trust bullets */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15, ease: [0.23, 1, 0.32, 1] }}
          className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-10"
        >
          {TRUST_BULLETS.map((bullet) => (
            <span key={bullet} className="flex items-center gap-2" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#339999' }} />
              {bullet}
            </span>
          ))}
        </motion.div>

        {/* Form card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25, ease: [0.23, 1, 0.32, 1] }}
          className="w-full max-w-md rounded-2xl p-8"
          style={{
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1.5px solid rgba(51,153,153,0.25)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Clinic Name */}
            <div>
              <label
                htmlFor="clinic_name"
                style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif' }}
              >
                Clinic Name
              </label>
              <input
                id="clinic_name"
                type="text"
                required
                maxLength={100}
                value={clinicName}
                onChange={(e) => setClinicName(e.target.value)}
                placeholder="e.g. Sunrise Family Practice"
                className="w-full mt-1.5 rounded-lg px-4 py-3 text-sm outline-none transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#ffffff',
                  fontFamily: 'Inter, sans-serif',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(51,153,153,0.5)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
              />
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif' }}
              >
                Your Email
              </label>
              <input
                id="email"
                type="email"
                required
                maxLength={255}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@clinic.com"
                className="w-full mt-1.5 rounded-lg px-4 py-3 text-sm outline-none transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#ffffff',
                  fontFamily: 'Inter, sans-serif',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(51,153,153,0.5)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 rounded-lg py-3.5 text-sm font-bold text-white transition-all"
              style={{
                backgroundColor: '#339933',
                boxShadow: '0 0 20px rgba(51,153,51,0.3)',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Create Your Account
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

        </motion.div>
      </div>
    </div>
  );
}
